import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

interface IMakeCircuitPaymentRequest {
  pin: string;
  customerAccountNumber: string;
  customerName: string;
  amount: number; // Amount in Kobo
  productId: string;
  customerPhoneNumber: string;
  reference: string;
  description: string;
  title: string;
  dateTime: string;
  customerEmail: string;
}

interface IMakeCircuitPaymentResponse {
  success?: boolean;
  message?: string;
  data: {
    redemptionLink: string;
  };
  errors?: IApiResponseError[];
}

const initialState: IApiState<IMakeCircuitPaymentResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const makeCircuitPaymentAsync = createAsyncThunk(
  'circuit/payment',
  async (values: IMakeCircuitPaymentRequest, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const accessToken = state.auth.value?.data?.token?.accessToken;
    const businessId = localStorage.getItem('businessId');
    const config = {
      headers: {
        Authorization: `Token ${accessToken}`,
        'x-business-id': businessId || '',
      },
    };
    try {
      const { data } = await api.post<IMakeCircuitPaymentResponse>(
        'billers/circuit/payment',
        values,
        config
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IMakeCircuitPaymentResponse>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const makeCircuitPaymentSlice = createSlice({
  name: 'circuit/payment',
  initialState,
  reducers: {
    resetMakeCircuitPayment(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(makeCircuitPaymentAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(makeCircuitPaymentAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload;
      })
      .addCase(makeCircuitPaymentAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IMakeCircuitPaymentResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetMakeCircuitPayment } = makeCircuitPaymentSlice.actions;

export default makeCircuitPaymentSlice.reducer;
